<template>
  <!-- Impersonating Warning Banner -->
  <div
    v-if="authStore.isImpersonating"
    class="bg-orange-500 text-white py-2 px-4"
  >
    <div class="max-w-7xl mx-auto flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <svg
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span class="font-medium"
          >โหมดทดสอบสิทธิ์: คุณกำลังทดสอบระบบในฐานะ {{ roleText }}</span
        >
      </div>
      <button
        @click="handleStopImpersonate"
        class="px-3 py-1 bg-white text-orange-600 rounded hover:bg-orange-50 font-medium text-sm transition-colors"
      >
        กลับสู่โหมดปกติ
      </button>
    </div>
  </div>

  <header class="bg-white shadow-sm border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-16">
        <!-- Mobile menu button -->
        <div class="flex items-center lg:hidden">
          <button
            @click="$emit('toggle-sidebar')"
            class="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          >
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        <!-- Page title -->
        <div class="flex-1 lg:flex-none">
          <h1 class="text-xl font-semibold text-gray-900 lg:hidden">
            {{ currentPageTitle }}
          </h1>
        </div>

        <!-- User controls and status -->
        <div class="flex items-center space-x-4">
          <!-- Thai Date Time Display -->
          <div class="hidden md:block">
            <ThaiClock type="header" :show-seconds="true" />
          </div>
          <!-- Connection status indicator -->
          <div class="flex items-center space-x-2">
            <span class="relative flex h-4 w-4"
              ><span
                class="bg-green-400 animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              ></span
              ><span
                class="bg-green-500 relative inline-flex rounded-full h-4 w-4 border-2 border-white"
              ></span
            ></span>
            <span class="text-sm text-gray-500 hidden sm:inline">
              เชื่อมต่อแล้ว
            </span>
          </div>

          <!-- User menu -->
          <div v-if="authStore.isAuthenticated" class="relative">
            <button
              @click="showUserMenu = !showUserMenu"
              class="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div
                class="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold"
              >
                {{ userInitials }}
              </div>
              <div class="hidden sm:block text-left">
                <p class="text-sm font-medium text-gray-700">
                  {{ authStore.user?.full_name }}
                </p>
                <p class="text-xs text-gray-500">{{ roleText }}</p>
              </div>
              <svg
                class="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <!-- Dropdown menu -->
            <div
              v-if="showUserMenu"
              @click.stop
              class="absolute right-0 mt-2 w-64 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
            >
              <div class="py-1">
                <!-- User Info -->
                <div class="px-4 py-3 border-b border-gray-100">
                  <p class="text-xs text-gray-500 mb-1">เข้าสู่ระบบในฐานะ</p>
                  <p class="text-sm font-medium text-gray-900">
                    {{ authStore.user?.full_name }}
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ authStore.user?.email }}
                  </p>
                  <div class="mt-2">
                    <span
                      class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {{ roleText }}
                    </span>
                  </div>
                </div>

                <!-- Impersonate Mode (Admin Only) -->
                <div
                  v-if="authStore.isAdmin && !authStore.isImpersonating"
                  class="px-4 py-3 border-b border-gray-100"
                >
                  <p class="text-xs font-medium text-gray-700 mb-2">
                    โหมดทดสอบสิทธิ์
                  </p>
                  <div class="space-y-2">
                    <button
                      @click="handleImpersonate('viewer')"
                      class="w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-between transition-colors"
                    >
                      <span>ทดสอบเป็น Viewer</span>
                      <svg
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button
                      @click="handleImpersonate('manager')"
                      class="w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-between transition-colors"
                    >
                      <span>ทดสอบเป็น Manager</span>
                      <svg
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Stop Impersonating (When Active) -->
                <div
                  v-if="authStore.isImpersonating"
                  class="px-4 py-3 border-b border-gray-100"
                >
                  <button
                    @click="handleStopImpersonate"
                    class="w-full px-3 py-2 text-sm bg-orange-100 hover:bg-orange-200 text-orange-700 rounded flex items-center justify-center space-x-2 transition-colors"
                  >
                    <svg
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      />
                    </svg>
                    <span class="font-medium">กลับสู่โหมดปกติ</span>
                  </button>
                </div>

                <!-- Logout Button -->
                <button
                  @click="handleLogout"
                  class="w-full text-left px-4 py-3 text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2 transition-colors"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span class="font-medium">ออกจากระบบ</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Login button for non-authenticated users -->
          <div v-else class="flex items-center space-x-2">
            <router-link
              to="/login"
              class="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
              <span>เข้าสู่ระบบ</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "../stores/authStore";

import ThaiClock from "./ThaiClock.vue";
import type { RouteMeta } from "../router";

// Emit events
defineEmits<{
  "toggle-sidebar": [];
}>();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const showUserMenu = ref(false);

// Get current page title from route meta
const currentPageTitle = computed(() => {
  const meta = route.meta as RouteMeta;
  return meta?.title || "Vehicle & Driver Management";
});

const userInitials = computed(() => {
  if (!authStore.user?.full_name) return "U";
  return authStore.user.full_name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
});

const roleText = computed(() => {
  const roleMap: Record<string, string> = {
    admin: "ผู้ดูแลระบบ",
    manager: "ผู้จัดการ",
    viewer: "ผู้ดู",
  };
  return roleMap[authStore.user?.role || "viewer"] || "ผู้ใช้";
});

const closeUserMenu = () => {
  showUserMenu.value = false;
};

const handleLogout = () => {
  authStore.logout();
  showUserMenu.value = false;
  router.push("/login");
};

const handleImpersonate = (role: "admin" | "manager" | "viewer") => {
  try {
    authStore.impersonateRole(role);
    showUserMenu.value = false;
    // Refresh to apply permission changes
    router.go(0);
  } catch (error) {
    console.error("Failed to impersonate:", error);
  }
};

const handleStopImpersonate = () => {
  authStore.stopImpersonating();
  showUserMenu.value = false;
  router.go(0);
};

// Handle click outside to close menu
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (showUserMenu.value && !target.closest(".relative")) {
    showUserMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>